import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';

const SidebarContainer = styled.aside`
  width: 280px;
  background: ${(p) => p.theme.surface};
  border-right: 1px solid ${(p) => p.theme.border};
  position: fixed;
  height: 100vh;
  left: 0;
  top: 0;
  z-index: 1000;
  overflow-y: auto;
  transition: background 0.2s ease, border-color 0.2s ease;

  @media (max-width: 768px) {
    transform: ${(props) => (props.isOpen ? 'translateX(0)' : 'translateX(-100%)')};
    transition: transform 0.3s ease;
  }
`;

const Logo = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${(p) => p.theme.border};

  h1 {
    font-size: 24px;
    font-weight: 700;
    color: ${(p) => p.theme.textPrimary};
    margin: 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const Nav = styled.nav`
  padding: 24px 0;
`;

const NavItem = styled.div`
  display: flex;
  align-items: center;
  padding: 12px 24px;
  cursor: pointer;
  transition: all 0.3s ease;
  border-right: 3px solid transparent;
  color: ${(props) => (props.active ? props.theme.navAccent : props.theme.textSecondary)};

  ${(props) =>
    props.active &&
    `
    background: ${props.theme.navActiveBg};
    border-right-color: ${props.theme.navAccent};
    color: ${props.theme.navAccent};
  `}

  &:hover {
    background: ${(props) => props.theme.navHoverBg};
    color: ${(props) => props.theme.navAccent};
  }

  svg {
    width: 20px;
    height: 20px;
    margin-right: 12px;
    opacity: 0.7;
  }

  span {
    font-weight: 500;
    font-size: 14px;
  }
`;

const Sidebar = ({ activeTab, setActiveTab }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const navItems = [
    {
      id: 'overview',
      label: 'Overview',
      path: '/dashboard',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <rect x="3" y="3" width="7" height="7"></rect>
          <rect x="14" y="3" width="7" height="7"></rect>
          <rect x="14" y="14" width="7" height="7"></rect>
          <rect x="3" y="14" width="7" height="7"></rect>
        </svg>
      )
    },
    {
      id: 'settings',
      label: 'Settings',
      path: '/dashboard/settings',
      icon: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="12" cy="12" r="3"></circle>
          <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
        </svg>
      )
    }
  ];

  const handleNavigation = (item) => {
    if (item.id === 'settings') {
      navigate(item.path);
    } else {
      setActiveTab(item.id);
      navigate(item.path);
    }
  };

  const isActive = (item) => {
    if (item.id === 'settings') {
      return location.pathname === '/dashboard/settings';
    }
    return activeTab === item.id && location.pathname === '/dashboard';
  };

  return (
    <SidebarContainer>
      <Logo>
        <h1>TradeBot</h1>
      </Logo>
      <Nav>
        {navItems.map(item => (
          <NavItem
            key={item.id}
            active={isActive(item)}
            onClick={() => handleNavigation(item)}
          >
            {item.icon}
            <span>{item.label}</span>
          </NavItem>
        ))}
      </Nav>
    </SidebarContainer>
  );
};

export default Sidebar;
